// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ArrowDown from "@ces/XDL/Icons/ArrowDown";
import ArrowUp from "@ces/XDL/Icons/ArrowUp";
import { color } from "@ces/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { Ref, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { Transition } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";
import styled, { css, ThemeProps } from "styled-components";

export interface FancyScrollRefProps {
    updateScrollable: () => void;
}

export interface FancyScrollProps {
    children: React.ReactElement;
    refElement: React.RefObject<HTMLElement>;
    wrapperRef?: React.RefObject<HTMLElement>;
    showArrow?: boolean;
    extendedStyles?: InjectableCss;
    color?: string;
    scrollByValue?: number;
    shadowHeight?: string;
    isBoxShadow?: boolean;
    paddingRight?: number;
    contentPaddingRight?: number;
}

export interface FancyScrollStyle {
    showArrowWrapper?: InjectableCss;
    showArrowIcon?: IconProps;
}

export const fancyScrollStyle: FancyScrollStyle = {
    showArrowWrapper: {
        css: css`
            border: 1px solid ${getColor("common.border")};
            border-radius: 4px;
            background-color: ${getColor("common.white")};
            height: 24px;
            margin: 8px;
            width: 100%;
        `,
    },
    showArrowIcon: {
        size: 11,
        color: "text.link",
        css: css`
            margin: auto;
            display: block;
        `,
    },
};
const styles = fancyScrollStyle;

const ScrollIndicator = styled.span<
    {
        direction: "up" | "down" | "left" | "right";
        transitionState: TransitionStatus;
        color?: string;
        shadowHeight?: string;
    } & ThemeProps<BaseTheme>
>`
    position: absolute;
    width: calc(100% - 8px);
    height: ${({ shadowHeight }) => shadowHeight || "12px"};
    z-index: 1;
    color: ${getColor("common.ui1")};
    display: flex;
    align-items: end;
    justify-content: center;
    transition: all ease-in-out 250ms;
    ${({ transitionState }) => {
        switch (transitionState) {
            case "entering":
                return "opacity: 0;";
            case "entered":
                return "opacity: 1;";
            case "exiting":
            case "exited":
                return "opacity: 0;";
            default:
                return "";
        }
    }}
    ${({ direction, color: customColor, shadowHeight = "12px" }) => {
        if (direction === "up") {
            return css`
                top: 0;
                align-items: start;
                background: linear-gradient(
                    180deg,
                    ${customColor ? color(customColor, 1) : color("common.white", 1)} 0%,
                    ${customColor ? color(customColor, 0) : color("common.white", 0)} 100%
                );
            `;
        }
        if (direction === "down") {
            return css`
                bottom: 0;
                background: linear-gradient(
                    0deg,
                    ${customColor ? color(customColor, 1) : customColor || color("common.white", 1)} 0%,
                    ${customColor ? color(customColor, 0) : customColor || color("common.white", 0)} 100%
                );
            `;
        }
        if (direction === "left") {
            return css`
                left: 0;
                top: 0;
                width: ${shadowHeight};
                height: calc(100% - 8px);
                & ~ button > span[direction="down"] {
                    bottom: 8px; /* scroll x width in case we have it */
                }
                background: linear-gradient(
                    90deg,
                    ${customColor ? color(customColor, 1) : customColor || color("common.white", 1)} 0%,
                    ${customColor ? color(customColor, 0) : customColor || color("common.white", 0)} 100%
                );
            `;
        }
        if (direction === "right") {
            return css`
                right: 0px;
                top: 0;
                width: ${shadowHeight};
                height: calc(100% - 8px);
                & ~ button > span[direction="down"] {
                    bottom: 8px; /* scroll x width in case we have it */
                }
                &:has(~ button > span[direction="up"]),
                &:has(~ button > span[direction="down"]) {
                    right: 8px; /* scroll y exist */
                }
                background: linear-gradient(
                    270deg,
                    ${customColor ? color(customColor, 1) : customColor || color("common.white", 1)} 0%,
                    ${customColor ? color(customColor, 0) : customColor || color("common.white", 0)} 100%
                );
            `;
        }
    }}
`;

const sum = (...a: number[]) => Math.ceil(a.reduce((previousValue, currentValue) => previousValue + currentValue, 0));

const FancyScroll = (
    {
        children,
        refElement,
        wrapperRef,
        showArrow,
        extendedStyles,
        color,
        scrollByValue,
        shadowHeight,
        isBoxShadow = false,
        paddingRight,
        contentPaddingRight,
    }: FancyScrollProps,
    ref: Ref<{ updateScrollable: () => void }>,
) => {
    const [showScrollDown, setShowScrollDown] = useState(false);
    const [showScrollUp, setShowScrollUp] = useState(false);
    const [showScrollRight, setShowScrollRight] = useState(false);
    const [showScrollLeft, setShowScrollLeft] = useState(false);

    useImperativeHandle(ref, () => ({
        updateScrollable() {
            setTimeout(() => checkScrollable());
        },
    }));
    const updateParentPadding = ({ hasVScroll }: { hasVScroll?: boolean } = {}) => {
        // if we need to stretch the body to remove padding
        if (hasVScroll) {
            if (wrapperRef?.current && paddingRight !== undefined) {
                wrapperRef.current.style.paddingRight = `${paddingRight}px`;
            }
            if (refElement.current && contentPaddingRight !== undefined) {
                refElement.current.style.paddingRight = `${contentPaddingRight}px`;
            }
        } else {
            if (wrapperRef?.current && paddingRight !== undefined) {
                wrapperRef.current.style.removeProperty("paddingRight");
            }
            if (refElement.current && contentPaddingRight !== undefined) {
                refElement.current.style.removeProperty("paddingRight");
            }
        }
    };
    const checkScrollable = useCallback(() => {
        if (refElement.current) {
            if (
                refElement.current.scrollHeight > refElement.current.offsetHeight &&
                refElement.current.scrollTop > 30
            ) {
                setShowScrollUp(true);
                updateParentPadding({ hasVScroll: true });
            } else {
                setShowScrollUp(false);
                updateParentPadding(); // reset
            }
            if (refElement.current.scrollHeight > sum(refElement.current.offsetHeight + refElement.current.scrollTop)) {
                setShowScrollDown(true);
                updateParentPadding({ hasVScroll: true });
            } else {
                setShowScrollDown(false);
                updateParentPadding(); // reset
            }
            if (isBoxShadow) {
                // In case we need x scrolling shadow
                if (refElement.current.scrollLeft > 30) {
                    setShowScrollLeft(true);
                } else {
                    setShowScrollLeft(false);
                }
                if (refElement.current.scrollWidth > refElement.current.scrollLeft + refElement.current.offsetWidth) {
                    setShowScrollRight(true);
                } else {
                    setShowScrollRight(false);
                }
            }
        }
    }, [refElement]);

    useEffect(() => {
        if (refElement && refElement.current) {
            setTimeout(() => checkScrollable());
            refElement.current.addEventListener("scroll", checkScrollable, { passive: true });
        }
        return () => {
            if (refElement && refElement.current) {
                refElement.current.removeEventListener("scroll", checkScrollable);
            }
        };
    }, [refElement]);

    return (
        <StyledWrapper
            css={css`
                position: relative;
                ${extendedStyles?.css}
            `}
        >
            <Transition mountOnEnter unmountOnExit in={showScrollLeft} timeout={{ enter: 75, exit: 250 }}>
                {state => (
                    <ScrollIndicator direction="left" transitionState={state} color={color} shadowHeight={shadowHeight}>
                        {showArrow && <IconMemo src={ChevronLeft} size={14} />}
                    </ScrollIndicator>
                )}
            </Transition>
            <Transition mountOnEnter unmountOnExit in={showScrollRight} timeout={{ enter: 75, exit: 250 }}>
                {state => (
                    <ScrollIndicator
                        direction="right"
                        transitionState={state}
                        color={color}
                        shadowHeight={shadowHeight}
                    >
                        {showArrow && <IconMemo src={ChevronRight} size={14} />}
                    </ScrollIndicator>
                )}
            </Transition>
            <Transition mountOnEnter unmountOnExit in={showScrollUp} timeout={{ enter: 75, exit: 250 }}>
                {state => (
                    <Clickable
                        onClick={() => {
                            refElement.current?.scrollBy({
                                top: scrollByValue ? -scrollByValue : 0,
                                behavior: "smooth",
                            });
                        }}
                        aria-label="arrow up"
                    >
                        <ScrollIndicator
                            direction="up"
                            transitionState={state}
                            color={color}
                            shadowHeight={shadowHeight}
                        >
                            {showArrow && !scrollByValue && <IconMemo src={ArrowDown} size={11} />}
                            {showArrow && scrollByValue && (
                                <StyledWrapper {...styles.showArrowWrapper}>
                                    <IconMemo {...styles.showArrowIcon} src={ArrowDown} />
                                </StyledWrapper>
                            )}
                        </ScrollIndicator>
                    </Clickable>
                )}
            </Transition>
            {children}
            <Transition mountOnEnter unmountOnExit in={showScrollDown} timeout={{ enter: 75, exit: 250 }}>
                {state => (
                    <Clickable
                        onClick={() => {
                            refElement.current?.scrollBy({
                                top: scrollByValue ?? 0,
                                behavior: "smooth",
                            });
                        }}
                        aria-label="arrow down"
                    >
                        <ScrollIndicator
                            direction="down"
                            transitionState={state}
                            color={color}
                            shadowHeight={shadowHeight}
                        >
                            {showArrow && !scrollByValue && <IconMemo src={ArrowDown} size={11} />}
                            {showArrow && scrollByValue && (
                                <StyledWrapper {...styles.showArrowWrapper}>
                                    <IconMemo {...styles.showArrowIcon} src={ArrowUp} />
                                </StyledWrapper>
                            )}
                        </ScrollIndicator>
                    </Clickable>
                )}
            </Transition>
        </StyledWrapper>
    );
};

export default React.forwardRef(FancyScroll);
